import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, X, User, Code, LogIn } from "lucide-react";
import { DeckBrowser } from "@/components/deck-library/DeckBrowser";
import { DeckFormModal } from "@/components/deck-library/DeckFormModal";
import { AdminDeckManagement } from "@/components/deck-library/AdminDeckManagement";
import { VerifiedUsersManagement } from "@/components/deck-library/VerifiedUsersManagement";
import type { Deck, CreateDeckData, UpdateDeckData } from "@/lib/api/deck-api";
import { createDeck, updateDeck, deleteDeck, fetchDecks } from "@/lib/api/deck-api";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitMap } from "@/lib/types/deck-builder";
import { useDataStore } from "@/lib/store/data-store";
import { useAuth } from "@/lib/store/auth-store";
import { toast } from "sonner";

export function DeckLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { units, divisions, loadingUnits, loadingDivisions } = useDataStore();
  const { user, loading: loadingUser, login } = useAuth();
  const [unitMap, setUnitMap] = useState<UnitMap>({});
  const [showForm, setShowForm] = useState(false);
  const [editingDeck, setEditingDeck] = useState<Deck | null>(null);
  const [showAdminView, setShowAdminView] = useState(false);
  const [adminTab, setAdminTab] = useState<"decks" | "verified-users">("decks");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (units) {
      const map: UnitMap = {};
      for (const unit of units) {
        map[unit.descriptorName] = unit;
      }
      setUnitMap(map);
    }
  }, [units]);

  // Check if form should be shown from URL params
  useEffect(() => {
    const create = searchParams.get("create");
    const edit = searchParams.get("edit");
    const code = searchParams.get("code");
    const adminParam = searchParams.get("admin");
    
    // Handle admin view from URL parameter
    if (adminParam === "verified-users" && user?.isAdmin) {
      setShowAdminView(true);
      setAdminTab("verified-users");
    }
    
    if (create === "true") {
      setShowForm(true);
      setEditingDeck(null);
    } else if (edit) {
      // Load deck for editing
      const deckId = parseInt(edit, 10);
      if (!isNaN(deckId)) {
        fetchDecks({ user_id: user?.discordId }).then(response => {
          const deck = response.data.find(d => d.id === deckId);
          if (deck) {
            setEditingDeck(deck);
            setShowForm(true);
          }
        }).catch(console.error);
      }
    } else if (code && user?.discordId) {
      // If code is provided, auto-open form for creating a new deck
      setShowForm(true);
      setEditingDeck(null);
    }
  }, [searchParams, user?.discordId, user?.isAdmin]);

  const handleCreateDeck = async (data: CreateDeckData | UpdateDeckData) => {
    try {
      if (editingDeck) {
        await updateDeck(editingDeck.id, data as UpdateDeckData);
        toast.success("Deck updated successfully");
      } else {
        await createDeck(data as CreateDeckData);
        toast.success("Deck created successfully");
      }
      // On success, close modal, clear editing deck, and clear URL params
      setShowForm(false);
      setEditingDeck(null);
      setSearchParams({});
      // Trigger refresh of deck list
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save deck");
      throw error;
    }
  };

  const handleDeleteDeck = async (deck: Deck) => {
    if (!confirm(`Are you sure you want to delete "${deck.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteDeck(deck.id);
      toast.success("Deck deleted successfully");
      // Trigger refresh of deck list
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete deck");
    }
  };

  const handleEditDeck = (deck: Deck) => {
    // Primary flow: Navigate to deck builder with deck code and ID
    navigate(`/deck-builder?code=${encodeURIComponent(deck.deck_code)}&deckId=${deck.id}&edit=true`);
  };

  const handleEditDeckWithCode = (deck: Deck) => {
    // Secondary flow: Edit via code input modal
    setEditingDeck(deck);
    setShowForm(true);
    setSearchParams({ edit: deck.id.toString() });
  };

  const handleModalOpenChange = (open: boolean) => {
    setShowForm(open);
    // If closing the modal and we were editing, clear the editing deck
    // But preserve code parameter for create flow
    if (!open && editingDeck) {
    setEditingDeck(null);
      // Clear edit parameter but preserve code if it exists
      const code = searchParams.get("code");
      if (code) {
        setSearchParams({ code });
      } else {
    setSearchParams({});
      }
    }
    // If closing and it was a create flow, preserve code in URL
    // (don't clear searchParams)
  };

  const handleLogin = (provider: "discord" | "dev") => {
    // Preserve the code parameter in sessionStorage so we can restore it after login
    const code = searchParams.get("code");
    const create = searchParams.get("create");
    
    if (code) {
      const returnUrl = `/deck-library?code=${encodeURIComponent(code)}${create ? `&create=${create}` : ""}`;
      sessionStorage.setItem("deckLibraryReturnUrl", returnUrl);
    }
    
    login(provider);
  };

  // Check for return URL after login
  useEffect(() => {
    if (user?.discordId && !showForm) {
      const returnUrl = sessionStorage.getItem("deckLibraryReturnUrl");
      if (returnUrl) {
        sessionStorage.removeItem("deckLibraryReturnUrl");
        // Extract code from returnUrl and set it in search params
        try {
          const url = new URL(returnUrl, window.location.origin);
          const code = url.searchParams.get("code");
          const create = url.searchParams.get("create");
          if (code) {
            const newParams = new URLSearchParams();
            newParams.set("code", code);
            if (create) {
              newParams.set("create", create);
            }
            setSearchParams(newParams);
          }
        } catch (e) {
          console.error("Failed to parse return URL:", e);
        }
      }
    }
  }, [user?.discordId, showForm, setSearchParams]);

  // Check if we should show login prompt (code present but user not authenticated)
  const code = searchParams.get("code");
  const create = searchParams.get("create");
  const showLoginPrompt = !loadingUser && code && !user?.discordId && (create === "true" || create === null);

  if (loadingUser || loadingDivisions || loadingUnits) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Deck Library</h1>
        {user?.discordId && (
          <Button onClick={() => {
            setShowForm(true);
            setEditingDeck(null);
            setSearchParams({ create: "true" });
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Deck
          </Button>
        )}
      </div>

      {showLoginPrompt && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              Login Required
            </CardTitle>
            <CardDescription>
              Please log in to save this deck to your library. Your deck code will be preserved.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" ? (
                <Button
                  variant="outline"
                  onClick={() => handleLogin("dev")}
                  className="border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-950"
                >
                  <Code className="h-4 w-4 mr-2" />
                  Dev Login
                </Button>
              ) : null}
              <Button
                variant="default"
                onClick={() => handleLogin("discord")}
              >
                <User className="h-4 w-4 mr-2" />
                Login with Discord
            </Button>
          </div>
          </CardContent>
        </Card>
      )}

      {user?.discordId && (
        <DeckFormModal
          open={showForm}
          onOpenChange={handleModalOpenChange}
            deck={editingDeck}
          initialDeckCode={searchParams.get("code") || undefined}
            divisions={divisions || []}
            unitMap={unitMap}
            discordUsername={user.name}
            isVerified={user.isVerified || false}
            onSubmit={handleCreateDeck}
          />
      )}

      {user?.isAdmin && (
        <div className="mb-4 flex items-center gap-2">
          <Button
            variant={showAdminView ? "default" : "outline"}
            onClick={() => setShowAdminView(!showAdminView)}
          >
            {showAdminView ? "Show Browse View" : "Show Admin Management"}
          </Button>
          {showAdminView && (
            <div className="flex gap-2 border rounded-md p-1">
              <Button
                variant={adminTab === "decks" ? "default" : "ghost"}
                size="sm"
                onClick={() => setAdminTab("decks")}
                className="rounded-md"
              >
                Decks
              </Button>
              <Button
                variant={adminTab === "verified-users" ? "default" : "ghost"}
                size="sm"
                onClick={() => setAdminTab("verified-users")}
                className="rounded-md"
              >
                Verified Users
              </Button>
            </div>
          )}
        </div>
      )}

      {user?.isAdmin && showAdminView ? (
        adminTab === "decks" ? (
        <AdminDeckManagement />
        ) : (
          <VerifiedUsersManagement />
        )
      ) : (
        <DeckBrowser
          currentUserId={user?.discordId}
          divisions={divisions || []}
          unitMap={unitMap}
          onEdit={handleEditDeck}
          onEditWithCode={handleEditDeckWithCode}
          onDelete={handleDeleteDeck}
          refreshKey={refreshKey}
        />
      )}
    </div>
  );
}

